import { Injectable } from '@angular/core';
import { LocaleService } from './locale.service';
import {
  IdentifierType,
  LoginOption,
  ParameterType,
  PasswordType,
  RegisterOption,
  USER_OF,
  UserParameter,
} from '../models/client-configuration';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { UntypedFormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { LoggerService } from './logger.service';

import { Util } from '../util';
import { ConfigService } from './config.service';
import { ApiService } from './api.service';
import { Cep } from '../models/cep';
import { Subject } from 'rxjs';
import { MAXLENGTH_DEPARTAMENTO } from '../models/user';

let registeredOptions: RegisterOption[] = [];

const DIBAM_RUT_STEP_OPTION_NAME = 'Ingreso mediante RUT';
const COUNTRY_CODE_CHILE = 'CL';

export function idValidator(control: UntypedFormControl, field: FormlyFieldConfig): ValidationErrors {
  if (control.parent) {
    let type = control.parent.controls['field']?.value;

    if (!type) {
      type = field.parent.fieldGroup[0].defaultValue;
    }

    switch (Number(type)) {
      case IdentifierType.DNI:
        return dniValidator(control);
      case IdentifierType.NIF:
        return nifValidator(control);
      case IdentifierType.NIE:
        return nieValidator(control);
      case IdentifierType.EMAIL:
        return emailValidator(control);
      case IdentifierType.RUT:
        return rutValidator(control);
    }

    return { identifier: true };
  }
}

export function dniValidator(control: UntypedFormControl): ValidationErrors {
  return !control.value || control.value.length !== 8 || !/^[0-9]*$/.test(control.value) ? null : { identifier: true };
}

export function nifValidator(control: UntypedFormControl): ValidationErrors {
  if (!control.value || control.value.length !== 9) {
    return null;
  }

  const value = control.value.slice(0, -1);
  const char = control.value[control.value.length - 1];

  if (!/^[0-9]*$/.test(value)) {
    return null;
  }

  return Util.NIF_CHARS.charAt(value % 23) !== char ? null : { identifier: true };
}

export function nieValidator(control: UntypedFormControl): ValidationErrors {
  if (!control.value || control.value.length !== 9) {
    return null;
  }

  let value: string = control.value.slice(1, -1);
  const char0 = control.value.toLowerCase().charAt(0);

  if (char0 === 'x') {
    value = '0' + value;
  } else if (char0 === 'y') {
    value = '1' + value;
  } else if (char0 === 'z') {
    value = '2' + value;
  }

  if (value.length !== 8 || !/^[0-9]*$/.test(value)) {
    return null;
  }

  const char = control.value[control.value.length - 1];
  return Util.NIF_CHARS.charAt(Number(value) % 23) !== char ? null : { identifier: true };
}

export function emailValidator(control: UntypedFormControl): ValidationErrors {
  return !control.value || !Util.EMAIL_PATTERN.test(control.value) ? null : { identifier: true };
}

export function rutValidator(control: UntypedFormControl): ValidationErrors {
  if (!control.value) {
    return null;
  }

  if (!Util.RUT_PATTERN.test(control.value)) {
    return null;
  }

  const inputValue: string = control.value.replace(/^0+|[^0-9kK]+/g, '').toLowerCase();

  const value: string = inputValue.slice(0, -1);
  const checkDigit: string = inputValue.slice(-1);

  let calculate = 0;
  let indexFactor: number = Util.RUT_FACTORS.length - 1;

  for (let i: number = value.length - 1; i >= 0; i--) {
    calculate = calculate + Util.RUT_FACTORS[indexFactor] * Number(value.substr(i, 1));
    indexFactor--;
  }

  const result: number = 11 - (calculate % 11);
  let digitChecker: string;

  if (result === 11) {
    digitChecker = '0';
  } else if (result === 10) {
    digitChecker = 'k';
  } else {
    digitChecker = result.toString();
  }

  return checkDigit === digitChecker ? { identifier: true } : null;
}

export function alphanumericValidator(control: UntypedFormControl): ValidationErrors {
  if (control.parent) {
    const type = control.parent.controls['field']?.value;

    switch (Number(type)) {
      case IdentifierType.OTRO:
      case IdentifierType.USER_ID:
      case IdentifierType.USER_OR_BARCODE:
      case IdentifierType.PASAPORTE:
      case IdentifierType.TARJETA_RESIDENCIA:
      case IdentifierType.MATRICULA:
      case IdentifierType.ASOCIADO:
      case IdentifierType.IBERIA:
      case IdentifierType.IDENTITY_CARD:
      case IdentifierType.CITIZENSHIP_CARD:
      case IdentifierType.BARCODE:
      case IdentifierType.CARD_NUMBER:
      case IdentifierType.LAST_4_DIGITS_PHONE:
      case IdentifierType.PARTNER_NUMBER:
        return !control.value || !Util.ALPHANUMERIC_PATTERN.test(control.value) ? null : { alphanumeric: true };
    }

    return { alphanumeric: true };
  }
}

export function domainValidator(control: UntypedFormControl, field: FormlyFieldConfig) {
  if (!(field.key as string).includes('register')) {
    return true;
  }

  let whiteListDomains = null;
  let blackListDomains = null;

  for (const registeredOption of registeredOptions) {
    const validDomains = registeredOption.fields.find((f) => f.tipoParametro === ParameterType.VALID_DOMAINS)
      ?.defaultValue;
    const invalidDomains = registeredOption.fields.find((f) => f.tipoParametro === ParameterType.INVALID_DOMAINS)
      ?.defaultValue;

    if (validDomains) {
      whiteListDomains = validDomains;
    }

    if (invalidDomains) {
      blackListDomains = invalidDomains;
    }
  }

  if (control.pristine) {
    return true;
  }

  if (!whiteListDomains && !blackListDomains) {
    return true;
  }

  if (!emailValidator(control)) {
    return false;
  }

  const userDomain = control.value.split('@')[1];

  let whiteListCondition = true;
  let blackListCondition = true;

  if (whiteListDomains) {
    const domains: string[] = whiteListDomains.split(';');
    whiteListCondition = domains.some((d) => d === userDomain);
  }

  if (blackListDomains) {
    const domains: string[] = blackListDomains.split(';');
    blackListCondition = domains.every((d) => d !== userDomain);
  }

  return whiteListCondition && blackListCondition;
}

export function getCep(
  control: UntypedFormControl,
  apiService: ApiService,
  event: Subject<Cep>,
  url: string
): ValidationErrors {
  return apiService.getCep(url, control.value).then((result) => {
    event.next(result);
    return !result.error;
  });
}

export function cepValidator(control: UntypedFormControl): ValidationErrors {
  return !control.value || !Util.CEP_PATTERN.test(control.value) ? null : { cep: true };
}

export const fieldsMC = [
  ParameterType.SEXO_MINISTERIO_COLOMBIA,
  ParameterType.POBLACION_MINISTERIO_COLOMBIA,
  ParameterType.PROVINCIA_MINISTERIO_COLOMBIA,
  ParameterType.CENTRO_MINISTERIO_COLOMBIA,
  ParameterType.CURSO_MINISTERIO_COLOMBIA,
];
const fieldMap: Map<ParameterType, string> = new Map([
  [ParameterType.IDENTIFICADOR_EXTERNO, 'id'],
  [ParameterType.NOMBRE, 'name'],
  [ParameterType.CORREO_ELECTRONICO, 'email'],
  [ParameterType.SEXO, 'gender'],
  [ParameterType.SEXO_MINISTERIO_COLOMBIA, 'gender'],
  [ParameterType.FECHA_NACIMIENTO, 'birthDate'],
  [ParameterType.DIRECCION, 'address'],
  [ParameterType.TIPO_DIRECCION, 'addressType'],
  [ParameterType.CODIGO_POSTAL, 'zipCode'],
  [ParameterType.POBLACION, 'city'],
  [ParameterType.POBLACION_MINISTERIO_COLOMBIA, 'city'],
  [ParameterType.PROVINCIA, 'state'],
  [ParameterType.PROVINCIA_MINISTERIO_COLOMBIA, 'state'],
  [ParameterType.PAIS, 'country'],
  [ParameterType.PAIS_ORIGEN, 'countryOrigin'],
  [ParameterType.TELEFONO, 'phone'],
  [ParameterType.FAX, 'fax'],
  [ParameterType.FOTO, 'profilePictureUrl'],
  [ParameterType.OBSERVACIONES, 'observations'],
  [ParameterType.CENTRO, 'centre'],
  [ParameterType.CENTRO_MINISTERIO_COLOMBIA, 'centre'],
  [ParameterType.ANNO_ACADEMICO, 'academicYear'],
  [ParameterType.DEPARTAMENTO, 'department'],
  [ParameterType.CICLO_FORMATIVO, 'trainingCycle'],
  [ParameterType.CURSO, 'course'],
  [ParameterType.CURSO_MINISTERIO_COLOMBIA, 'course'],
  [ParameterType.GRUPO_DEL_CURSO, 'courseGroup'],
  [ParameterType.EMPRESA, 'company'],
  [ParameterType.PUESTO, 'position'],
  [ParameterType.NOMBRE_CONTACTO, 'contactPerson'],
  [ParameterType.ROL, 'role'],
  [ParameterType.CATEGORIA, 'category'],
  [ParameterType.AFILIADO, 'affiliated']
]);

@Injectable({
  providedIn: 'root',
})
export class FormBuilderService {
  loginLibrarySelect$ = new Subject<any>();
  loginSubmit$ = new Subject<any>();
  registerLibrarySelect$ = new Subject<any>();
  genericSelect$ = new Subject<any>();
  eventGetCep$ = new Subject<Cep>();
  showSimplifiedLogin: boolean;

  codeSelectorValues: any[];

  linkedSelectValues: Map<string, any[]> = new Map<string, any[]>();

  URL_API_GATEWAY_QUERY_CEP: string;

  constructor(
    private logger: LoggerService,
    private localeService: LocaleService,
    private configService: ConfigService,
    private apiService: ApiService
  ) {
    this.showSimplifiedLogin = this.configService.get().showSimplifiedLogin;
    this.URL_API_GATEWAY_QUERY_CEP = this.configService.get().urlApiGatewayQueryCep;
  }
  private static isLoginParameters(param: any): param is LoginOption {
    return 'integration' in param[0];
  }

  public static getSelectLabel(type: number): string {
    switch (type) {
      case IdentifierType.DNI:
        return 'user.fields.types.dni';
      case IdentifierType.NIF:
        return 'user.fields.types.nif';
      case IdentifierType.NIE:
        return 'user.fields.types.nie';
      case IdentifierType.PASAPORTE:
        return 'user.fields.types.passport';
      case IdentifierType.TARJETA_RESIDENCIA:
        return 'user.fields.types.residence';
      case IdentifierType.MATRICULA:
        return 'user.fields.types.enrollment';
      case IdentifierType.ASOCIADO:
        return 'user.fields.types.associated';
      case IdentifierType.OTRO:
        return 'user.fields.types.other';
      case IdentifierType.SIN: // ¿?
        return 'user.fields.types.none';
      case IdentifierType.IBERIA:
        return 'user.fields.types.generic';
      case IdentifierType.EMAIL:
        return 'user.fields.types.email';
      case IdentifierType.IDENTITY_CARD:
        return 'user.fields.types.identityCard';
      case IdentifierType.CITIZENSHIP_CARD:
        return 'user.fields.types.citizenshipCard';
      case IdentifierType.RUT:
        return 'user.fields.types.rut';
      default:
        return 'user.fields.types.unknown';
    }
  }

  parse(params: RegisterOption[] | LoginOption[], isEleo = false): FormlyFieldConfig[][] {
    if (params.length > 0 && !('integration' in params[0])) {
      registeredOptions = params as RegisterOption[];
    }
    // TODO: factory!
    if (FormBuilderService.isLoginParameters(params)) {
      return this._parseLoginParameters(<LoginOption[]>params);
    } else {
      return this._parseRegisterParameters(<RegisterOption[]>params, isEleo);
    }
  }

  populateSelect(accountParameters: RegisterOption[], formFields: FormlyFieldConfig[][], selectedForm: number, e: any) {
    const field: FormlyFieldConfig = e.field;
    const targetField = accountParameters[selectedForm].fields.find((f) => f.tipoParametro.toString() === field.key);
    const childType = targetField.tipoParametroHijo;

    if (!childType) {
      field.props.disabled = false;
      return;
    }

    const child = this.getFormField(formFields, selectedForm, childType);

    if (!child) {
      this.logger.warn(`No child found for type ${childType}, this shouldn't have happened...`);
      return;
    }

    child.props.disabled = false;

    if (this.linkedSelectValues.has(child.key as string)) {
      child.props.options = this.linkedSelectValues.get(child.key as string);
    }

    const group = e.field.props.options.filter((o) => o.label === e.value)[0];

    if (group) {
      child.props.options = (child.props.options as any[])
        .filter((f) => f.group === group.value)
        .map((f) => ({ value: f.value, label: f.label }));
    }

    const childField = accountParameters[selectedForm].fields.find((f) => f.tipoParametro.toString() === child.key);

    if (childField.tipoParametroHijo) {
      const target = this.getFormField(formFields, selectedForm, childField.tipoParametroHijo);

      if (target) {
        target.props.options = [];
        target.props.disabled = true;
      }
    }
  }

  getFormField(
    accountFields: FormlyFieldConfig[][],
    selectedForm: number,
    parameterType: ParameterType
  ): FormlyFieldConfig {
    return accountFields[selectedForm]
      ? accountFields[selectedForm].find((f) => f.key === parameterType.toString())
      : null;
  }

  getLinkedSelectReversedValue(type: ParameterType, label: string): number | string {
    if (this.linkedSelectValues.has(type.toString())) {
      const target = this.linkedSelectValues.get(type.toString()).find((map) => map.label === label);

      if (target) {
        return target.label;
      }
    }

    return label;
  }

  private _parseLoginParameters(loginOptions: LoginOption[]): FormlyFieldConfig[][] {
    const fields: FormlyFieldConfig[][] = [];

    const keyCount = 0;
    loginOptions.forEach((loginOption) => {
      const loginField: FormlyFieldConfig[] = [];
      if (this.configService.isIberoamericana()) {
        const userTypeSelector = this.buildLoginUserTypes();
        loginField.push(userTypeSelector);
      }
      const identifiers = this._parseLoginIdentifiers(loginOption);

      if (identifiers.length > 0) {
        loginField.push(this._buildLoginIdentifier(identifiers, keyCount));
      }

      const passwords: any[] = this._parseLoginPasswords(loginOption);

      if (passwords.length > 0) {
        loginField.push(...this._buildLoginPassword(passwords));
      }

      fields.push(loginField);
    });

    if (fields.length > 1) {
      let values: any[];
      if (this.configService.isClientDibam()) {
        values = loginOptions.map((o, i) => ({ value: i, label: o.name }));
      } else {
        values = loginOptions.map((o) => ({ value: o.library, label: o.name }));
      }

      fields.forEach((f) => {
        f.unshift({
          key: 'library',
          type: values.length >= 10 ? 'custom-select' : 'select',
          defaultValue: values[0].value,
          className: 'form-select',
          props: {
            label: this._getLogInLibrarySelectionLabel(),
            required: true,
            options: values,
            change: (_, $event) => this.loginLibrarySelect$.next($event),
          },
        });
      });
    }

    return fields;
  }

  private _getSignUpLibrarySelectionLabel(): string {
    if (this.configService.isClientDibam()) {
      return this.localeService.instant('login.dibamSelectSignUpMode');
    }
    return this.localeService.instant('login.selectLibrary');
  }

  private _getLogInLibrarySelectionLabel(): string {
    if (this.configService.isClientDibam()) {
      return this.localeService.instant('login.dibamSelectLogInMode');
    }
    return this.localeService.instant('login.selectLibrary');
  }

  private _parseLoginIdentifiers(loginOption: LoginOption): any[] {
    const translate = this.localeService;
    const identifiers: any[] = [];

    loginOption.fields.forEach((field) => {
      switch (field) {
        case 'DNI':
          identifiers.push({ value: IdentifierType.DNI.valueOf(), label: translate.instant('user.fields.types.dni') });
          break;
        case 'NIF':
          identifiers.push({ value: IdentifierType.NIF.valueOf(), label: translate.instant('user.fields.types.nif') });
          break;
        case 'NIE':
          identifiers.push({ value: IdentifierType.NIE.valueOf(), label: translate.instant('user.fields.types.nie') });
          break;
        case 'PASAPORTE':
          identifiers.push({
            value: IdentifierType.PASAPORTE.valueOf(),
            label: translate.instant('user.fields.types.passport'),
          });
          break;
        case 'TARJETA_RESIDENCIA':
          identifiers.push({
            value: IdentifierType.TARJETA_RESIDENCIA.valueOf(),
            label: translate.instant('user.fields.types.residence'),
          });
          break;
        case 'MATRICULA':
          identifiers.push({
            value: IdentifierType.MATRICULA.valueOf(),
            label: translate.instant('user.fields.types.enrollment'),
          });
          break;
        case 'ASOCIADO':
          identifiers.push({
            value: IdentifierType.ASOCIADO.valueOf(),
            label: translate.instant('user.fields.types.associated'),
          });
          break;
        case 'SIN':
          identifiers.push({ value: IdentifierType.SIN.valueOf(), label: translate.instant('user.fields.types.none') });
          break;
        case 'IBERIA':
          identifiers.push({
            value: IdentifierType.IBERIA.valueOf(),
            label: translate.instant('user.fields.types.generic'),
          });
          break;
        case 'EMAIL':
          identifiers.push({
            value: IdentifierType.EMAIL.valueOf(),
            label: translate.instant('user.fields.types.email'),
          });
          break;
        case 'IDENTITY_CARD':
          identifiers.push({
            value: IdentifierType.IDENTITY_CARD.valueOf(),
            label: translate.instant('user.fields.types.identityCard'),
          });
          break;
        case 'CITIZENSHIP_CARD':
          identifiers.push({
            value: IdentifierType.CITIZENSHIP_CARD.valueOf(),
            label: translate.instant('user.fields.types.citizenshipCard'),
          });
          break;
        case 'RUT':
          identifiers.push({ value: IdentifierType.RUT.valueOf(), label: translate.instant('user.fields.types.rut') });
          break;
        case 'USER_ID':
          identifiers.push({ value: IdentifierType.USER_ID.valueOf(), label: translate.instant('user.fields.id') });
          break;
        case 'LAST_NAME':
          identifiers.push({
            value: IdentifierType.LAST_NAME.valueOf(),
            label: translate.instant('user.fields.lastName'),
          });
          break;
        case 'FULL_NAME':
          identifiers.push({
            value: IdentifierType.FULL_NAME.valueOf(),
            label: translate.instant('user.fields.fullName'),
          });
          break;
        case 'BARCODE':
          identifiers.push({
            value: IdentifierType.BARCODE.valueOf(),
            label: translate.instant('user.fields.barcode'),
          });
          break;
        case 'USER_OR_BARCODE':
          identifiers.push({
            value: IdentifierType.USER_OR_BARCODE.valueOf(),
            label: translate.instant('user.fields.userOrBarcode'),
          });
          break;
        case 'CARD_NUMBER':
          identifiers.push({
            value: IdentifierType.CARD_NUMBER.valueOf(),
            label: translate.instant('user.fields.types.associated'),
          });
          break;
        case 'LAST_4_DIGITS_PHONE':
          identifiers.push({
            value: IdentifierType.LAST_4_DIGITS_PHONE.valueOf(),
            label: translate.instant('user.fields.digitsPhone'),
          });
          break;
        case 'PARTNER_NUMBER':
          identifiers.push({
            value: IdentifierType.PARTNER_NUMBER.valueOf(),
            label: translate.instant('user.fields.partnerNumber'),
          });
          break;
        case 'USER':
          identifiers.push({ value: IdentifierType.USER_ID.valueOf(), label: translate.instant('user.fields.id') });
          break;
        case 'EXTERNAL_URL':
        case 'PASSWORD':
        case 'PIN':
        case 'AZTECA_SIGNATURE':
        case 'AZTECA':
        case 'LAST':
        case 'USERTYPE':
          break;
        default:
          // TODO: meh
          if (field.startsWith('EXTERNAL_URL')) {
            break;
          }

          if (this.configService.isClientDibam() && field.includes('RUT ')) {
            identifiers.unshift({ value: IdentifierType.OTRO.valueOf(), label: field });
            break;
          }

          identifiers.push({ value: IdentifierType.OTRO.valueOf(), label: field });
          break;
      }
    });

    return identifiers;
  }

  private _buildLoginIdentifier(identifiers: any[], keyCount: number): FormlyFieldConfig {
    const translate = this.localeService;

    const identifierField: FormlyFieldConfig = {
      key: 'loginId' + keyCount,
      className: 'composed-select-input',
      fieldGroup: [
        {
          key: 'field',
          type: 'select',
          defaultValue: identifiers[0].value,
          className: 'composed-select-input__left',
          props: {
            disabled: identifiers.length === 1,
            label: translate.instant('user.fields.id'),
            required: true,
            options: identifiers,
            change: (_field, _) => {
              const formControl = _field.parent.fieldGroup[1].formControl as UntypedFormControl;
              formControl.updateValueAndValidity();
            },
          },
        },
        {
          key: 'data',
          type: 'input',
          className: 'composed-select-input__right',
          validators: {
            identifier: idValidator,
            domain: domainValidator,
          },
          validation: {
            messages: {
              identifier: translate.instant('login.wrongIdFormat'),
              domain: translate.instant('login.invalidDomain'),
            },
          },
          props: {
            required: true,
            label: translate.instant('user.fields.id'),
          },
        },
      ],
    };

    if (identifiers.length === 1 || this.showSimplifiedLogin) {
      delete identifierField.className;
      const label = this._getIdentifierLabel(identifiers);
      identifierField.fieldGroup[0].hide = true;
      identifierField.fieldGroup[1].props.label = label;
    }

    return identifierField;
  }

  private _getIdentifierLabel(identifiers: any[]) {
    const labelForSiplifiedLogin = identifiers.map((identifier) => identifier.label).join(' / ');
    return this.showSimplifiedLogin ? labelForSiplifiedLogin : identifiers[0].label;
  }

  private _parseLoginPasswords(loginOption: LoginOption): any[] {
    const translate = this.localeService;
    const passwords: any[] = [];

    loginOption.fields.forEach((field) => {
      switch (field) {
        case 'PASSWORD':
          passwords.push({ value: PasswordType.PASSWORD.valueOf(), label: translate.instant('user.fields.password') });
          break;
        case 'PIN':
          passwords.push({ value: PasswordType.PIN.valueOf(), label: translate.instant('user.fields.pin') });
          break;
        case 'AZTECA_SIGNATURE':
          passwords.push({
            value: PasswordType.AZTECA_SIGNATURE.valueOf(),
            label: translate.instant('user.fields.aztecaSignature'),
          });
          break;
        case 'AZTECA':
          passwords.push({ value: PasswordType.AZTECA.valueOf(), label: translate.instant('user.fields.azteca') });
          break;
        case 'LAST':
          passwords.push({ value: PasswordType.LAST.valueOf(), label: translate.instant('user.fields.last') });
          break;
      }
    });

    return passwords;
  }

  private _buildLoginPassword(passwords: any[]): FormlyFieldConfig[] {
    const passwordsField: FormlyFieldConfig[] = [];

    passwords.forEach((password) => {
      const passwordField: FormlyFieldConfig = {
        key: 'password',
        type: 'custom-password',
        props: {
          required: true,
          type: 'password',
          label: password.label,
          attributes: {
            autocomplete: 'current-password',
          },
          keypress: (_field, event: KeyboardEvent) => {
            if (_field.form.valid && event.key === 'Enter') {
              this.loginSubmit$.next(true);
            }
          },
        },
      };

      passwordsField.push(passwordField);
    });

    return passwordsField;
  }

  public shouldLoadRutStep(registerOptionName: string) {
    return this.configService.isClientDibam() && registerOptionName.trim() === DIBAM_RUT_STEP_OPTION_NAME;
  }

  public parseOptionFields(registerOption: RegisterOption): FormlyFieldConfig[] {
    const fields: FormlyFieldConfig[] = [];
    registerOption.fields.forEach((param) => {
      const parsed = this._parseRegisterParameter(param);
      if (parsed) {
        fields.push(parsed);
      }
    });
    return fields;
  }

  public getRutFirstStepConfig(option: RegisterOption): FormlyFieldConfig[] {
    const rutStepOption: RegisterOption = {
      library: option.library,
      name: option.name,
      fields: [this.getRutRegisterParam(option)],
      multipleId: option.multipleId,
    };

    const config = this.parseOptionFields(rutStepOption);
    const codeSelectorConfig = this.getCodeSelectorConfig(this.codeSelectorValues[0], this.codeSelectorValues);
    config.unshift(codeSelectorConfig);
    return config;
  }

  private _parseRegisterParameters(params: RegisterOption[], isEleo = false): FormlyFieldConfig[][] {
    const fieldsConfig: FormlyFieldConfig[][] = [];

    if (isEleo) {
      const p = { ...params[0] };
      p.library = '-1';
      p.multipleId = -1;
      p.name = this.localeService.instant('general.selectAnOption');
      params.unshift(p);
    }

    params.forEach((option) => {
      const optionFieldsConfig = this.parseOptionFields(option);
      fieldsConfig.push(optionFieldsConfig);
    });

    fieldsConfig.map((ff) => {
      ff.forEach((f) => {
        if (f.key === ParameterType.IDENTIFICADOR_EXTERNO.toString()) {
          const fieldGroup = f.fieldGroup;
          const options = fieldGroup[0].props.options as any[];
          const alias = ff.find((_f) => _f.key === ParameterType.VALUE_ALIAS.toString());

          if (options.length === 1) {
            delete f.className;
            fieldGroup[0].hide = true;

            if (alias && alias.defaultValue) {
              fieldGroup[1].props.label = alias.defaultValue;
            } else {
              fieldGroup[1].props.label = options[0].label;
            }
          } else {
            if (alias && alias.defaultValue) {
              options.find((o) => o.value === IdentifierType.OTRO.toString()).label = alias.defaultValue;
            }
          }
        }
        if (f.hide) {
          f.props.label = 'hide';
        }
      });

      const observationsIdx = ff.findIndex((z) => z.key === ParameterType.OBSERVACIONES.toString());

      if (observationsIdx !== -1) {
        ff.push(ff.splice(observationsIdx, 1)[0]);
      }
    });

    if (fieldsConfig.length > 1) {
      const values: any[] = params.map((o) => ({
        value: o.multipleId == null ? o.library : o.multipleId,
        label: o.name,
      }));
      this.codeSelectorValues = values;

      fieldsConfig.forEach((f) => {
        const codeSelectorConfig = this.getCodeSelectorConfig(values[0].value, values);
        f.unshift(codeSelectorConfig);
      });
    }

    if (isEleo) {
      fieldsConfig[0].map((f) => {
        if (f.key === '3') {
          return;
        }
        f.hide = true;
      });
    }
    return fieldsConfig;
  }

  public getCodeSelectorConfig(defaultValue: string, options: any[]): FormlyFieldConfig {
    return {
      key: ParameterType.CODIGO.toString(),
      type: 'select',
      defaultValue: defaultValue,
      className: 'form-select',
      props: {
        label: this._getSignUpLibrarySelectionLabel(),
        required: true,
        options: options,
        change: (_, $event) => this.registerLibrarySelect$.next($event),
      },
    };
  }

  toUserField(key: number): string {
    return fieldMap.get(key);
  }

  getFieldMap() {
    return fieldMap;
  }

  getLabelByKey(parameterType: ParameterType, model: any): string {
    const values = this.linkedSelectValues.get(parameterType.toString());

    if (!values) {
      return null;
    }

    const value = values.find((v) => v.label === model[parameterType]);
    return value?.label;
  }

  adaptMoECParams(model: any): void {
    if (model[ParameterType.PROVINCIA_MINISTERIO_COLOMBIA]) {
      model[ParameterType.PROVINCIA_MINISTERIO_COLOMBIA] = this.getLabelByKey(
        ParameterType.PROVINCIA_MINISTERIO_COLOMBIA,
        model
      );
    }

    if (model[ParameterType.POBLACION_MINISTERIO_COLOMBIA]) {
      model[ParameterType.POBLACION_MINISTERIO_COLOMBIA] = this.getLabelByKey(
        ParameterType.POBLACION_MINISTERIO_COLOMBIA,
        model
      );
    }

    if (model[ParameterType.CENTRO_MINISTERIO_COLOMBIA]) {
      model[ParameterType.CENTRO_MINISTERIO_COLOMBIA] = this.getLabelByKey(
        ParameterType.CENTRO_MINISTERIO_COLOMBIA,
        model
      );
    }

    if (model[ParameterType.CURSO_MINISTERIO_COLOMBIA]) {
      model[ParameterType.CURSO_MINISTERIO_COLOMBIA] = model[ParameterType.CURSO_MINISTERIO_COLOMBIA];
    }

    if (model[ParameterType.SEXO_MINISTERIO_COLOMBIA]) {
      model[ParameterType.SEXO_MINISTERIO_COLOMBIA] = model[ParameterType.SEXO_MINISTERIO_COLOMBIA];
    }

    if (model[ParameterType.ROL]) {
      model[ParameterType.ROL] = this.getLabelByKey(ParameterType.ROL, model) ?? model[ParameterType.ROL];
    }
  }

  setValueSaoPaulo(model: any, accountModel: any, cep: Cep, formGroup: FormGroup): void {
    const translate = this.localeService;
    for (const accountField of model) {
      switch (accountField.key) {
        case ParameterType.DIRECCION.toString():
          if (cep.direction === '') {
            accountField.props.label = translate.instant('user.fields.address');
          } else {
            accountField.props.label = cep.direction;
            formGroup.get(ParameterType.DIRECCION.toString()).setValue(cep.direction);
          }
          break;
        case ParameterType.TIPO_DIRECCION.toString():
          if (cep.typeOfAddress === '') {
            accountField.props.label = translate.instant('user.fields.addressType');
          } else {
            accountField.props.label = cep.typeOfAddress;
            formGroup.get(ParameterType.TIPO_DIRECCION.toString()).setValue(cep.typeOfAddress);
          }
          break;
        case ParameterType.POBLACION.toString():
          if (cep.population === '') {
            accountField.props.label = translate.instant('user.fields.city');
          } else {
            accountField.props.label = cep.population;
            formGroup.get(ParameterType.POBLACION.toString()).setValue(cep.population);
          }
          break;
        case ParameterType.PROVINCIA.toString():
          if (cep.province === '') {
            accountField.props.label = translate.instant('user.fields.state');
          } else {
            accountField.props.label = cep.province;
            formGroup.get(ParameterType.PROVINCIA.toString()).setValue(cep.province);
          }
          break;
      }
    }
  }

  private getRutRegisterParam(option: RegisterOption): UserParameter {
    const rutRegisterParam: UserParameter = {
      tipoParametro: ParameterType.IDENTIFICADOR_EXTERNO,
      alta: true,
      modificacion: false,
      obligatorio: true,
      defaultValue: IdentifierType.RUT.toString(),
      multipleId: option.multipleId,
    };
    return rutRegisterParam;
  }

  private _parseRegisterParameter(param: UserParameter): FormlyFieldConfig {
    const translate = this.localeService;
    const type = param.tipoParametro.toString();

    const field: FormlyFieldConfig = {
      key: type,
      type: 'input',
      defaultValue: param.defaultValue,
      props: {
        required: param.obligatorio,
      },
    };

    if (param.valueMap && param.valueMap.length) {
      field.type = 'custom-select';

      if (param.tipoParametroPadre) {
        field.props.disabled = true;
      }

      const selectValues = param.valueMap.flatMap((e) =>
        e.values.map((v) => {
          const valueMap = {
            value: v.value,
            label: v.label,
          };

          if (e.parentId !== -1) {
            valueMap['group'] = e.parentId;
          }

          return valueMap;
        })
      );

      this.linkedSelectValues.set(field.key as string, selectValues);

      field.props.options = selectValues;
      field.props.change = (f, $event) => this.genericSelect$.next({ field: f, value: $event.value });
    }

    // TODO: factory
    switch (param.tipoParametro) {
      case ParameterType.IDENTIFICADOR_EXTERNO:
        const options: any[] = param.defaultValue.split(';').map((v) => {
          const key = FormBuilderService.getSelectLabel(Number(v));
          return { value: v, label: translate.instant(key) };
        });

        if (this.configService.isClientDibam()) {
          this.sortAvailableIdentifierOptionsDibam(options);
        }

        return {
          key: type,
          className: 'composed-select-input',
          fieldGroup: [
            {
              key: `${type}.field`,
              type: 'select',
              defaultValue: options[0].value,
              className: 'composed-select-input__left',
              props: {
                disabled: options.length === 1,
                label: translate.instant('user.fields.id'),
                required: param.obligatorio,
                options: options,
                change: (_field, _) => {
                  const formControl = _field.parent.fieldGroup[1].formControl as UntypedFormControl;
                  formControl.updateValueAndValidity();
                },
              },
            },
            {
              key: `${type}.data-register`,
              type: 'input',
              className: 'composed-select-input__right',
              validators: {
                identifier: idValidator,
                alphanumeric: alphanumericValidator,
                domain: domainValidator,
              },
              validation: {
                messages: {
                  identifier: translate.instant('login.wrongIdFormat'),
                  alphanumeric: translate.instant('login.userNameFormat'),
                  domain: translate.instant('login.invalidDomain'),
                },
              },
              props: {
                required: true,
              },
            },
          ],
        };
      case ParameterType.NOMBRE:
        if (this.configService.isClientDibam()) {
          field.props.label = translate.instant('user.fields.name');
        } else {
          field.props.label = translate.instant('user.fields.fullName');
        }
        field.props.attributes = { autocomplete: 'name' };
        return field;
      case ParameterType.OBSERVACIONES:
        field.props.label = translate.instant('user.fields.observations');
        field.type = 'textarea';
        field.className = 'formly-textarea';
        return field;
      case ParameterType.FOTO:
        field.props.label = translate.instant('user.fields.picture');
        field.type = 'file';
        return field;
      case ParameterType.ATTACH_FILES:
        field.type = 'file';
        field.props.floatLabel = 'never';
        return field;
      case ParameterType.CONTRASENNA:
        return {
          key: type,
          validators: {
            fieldMatch: {
              expression: (control: UntypedFormControl) => {
                const value = control.value[type];
                return value && (value.data === value.field || !value.data || !value.field);
              },
              message: translate.instant('login.passwordNotMatching'),
              errorPath: `${type}.data`,
            },
          },
          fieldGroup: [
            {
              key: `${type}.field`,
              type: 'custom-password',
              validators: {
                passwordStrength: {
                  expression: (control: UntypedFormControl) => control.value && Util.PASSWORD_PATTERN.test(control.value),
                  message: translate.instant('login.passwordFormat'),
                },
              },
              props: {
                label: translate.instant('user.fields.password'),
                type: 'password',
                required: param.obligatorio,
                attributes: {
                  autocomplete: 'new-password',
                },
              },
            },
            {
              key: `${type}.data`,
              type: 'custom-password',
              props: {
                label: translate.instant('user.fields.confirmPassword'),
                type: 'password',
                required: true,
                attributes: {
                  autocomplete: 'new-password',
                },
              },
            },
          ],
        };
      case ParameterType.SEXO_MINISTERIO_COLOMBIA:
      case ParameterType.SEXO:
        field.type = 'select';
        field.props.label = translate.instant('user.fields.gender.title');
        field.props.options = this.getGenderValues(param.tipoParametro);
        return field;
      case ParameterType.FECHA_NACIMIENTO:
        field.type = 'datepicker';
        field.props.label = translate.instant('user.fields.birthDate');
        field.props.attributes = { autocomplete: 'bday' };
        return field;
      case ParameterType.PAIS_ORIGEN:
      case ParameterType.PAIS:
        const countryLabel = this.getCountryLabel(param);
        const countries = this.localeService.instant('countries');
        const countryOpts: object[] = Object.keys(countries).map((k) => ({ value: k, label: countries[k] }));

        field.type = 'select';
        field.props.label = translate.instant(countryLabel);
        field.props.options = countryOpts;
        return field;
      case ParameterType.TIPO_DIRECCION:
        if (this.configService.isSaoPaulo()) {
          field.props.disabled = true;
        }
        field.props.label = translate.instant('user.fields.addressType');
        return field;
      case ParameterType.NOMBRE_CONTACTO:
        if (this.configService.isClientDibam()) {
          field.props.label = translate.instant('user.fields.lastName1');
        } else {
          field.props.label = translate.instant('user.fields.contactPerson');
        }
        return field;
      case ParameterType.DIRECCION:
        if (this.configService.isSaoPaulo()) {
          field.props.disabled = true;
        }
        field.props.label = translate.instant('user.fields.address');
        field.props.attributes = { autocomplete: 'street-address' };
        return field;
      case ParameterType.POBLACION:
        if (this.configService.isSaoPaulo()) {
          field.props.disabled = true;
        }
        field.props.label = translate.instant('user.fields.city');
        field.props.attributes = { autocomplete: 'home' };
        return field;
      case ParameterType.PROVINCIA:
        if (this.configService.isSaoPaulo()) {
          field.props.disabled = true;
        }
        field.props.label = translate.instant('user.fields.state');
        field.props.attributes = { autocomplete: 'address-level2' };
        return field;
      case ParameterType.TELEFONO:
        field.props.label = translate.instant('user.fields.phone');
        field.props.attributes = { autocomplete: 'tel' };
        field.props.pattern = Util.NUMBERS_ONLY_PATTERN;
        return field;
      case ParameterType.FAX:
        if (this.configService.isClientDibam()) {
          field.props.label = translate.instant('user.fields.lastName2');
        } else {
          field.props.label = translate.instant('user.fields.fax');
          field.props.attributes = { autocomplete: 'fax' };
          field.props.pattern = Util.NUMBERS_ONLY_PATTERN;
        }
        return field;
      case ParameterType.CODIGO_POSTAL:
        if (this.configService.isSaoPaulo()) {
          return this.getSaoPauloCepFieldConfig();
        }
        field.props.label = translate.instant('user.fields.postalCode');
        field.props.attributes = { autocomplete: 'postal-code' };
        return field;
      case ParameterType.CORREO_ELECTRONICO:
        field.props.label = translate.instant('user.fields.email');
        field.props.attributes = { autocomplete: 'email' };
        field.props.pattern = Util.EMAIL_PATTERN;
        field.validators = { domain: domainValidator };
        field.validation = { messages: { domain: translate.instant('login.invalidDomain') } };
        return field;
      case ParameterType.ANNO_ACADEMICO:
        field.props.label = translate.instant('user.fields.academicYear');
        field.props.pattern = Util.NUMBERS_ONLY_PATTERN;
        return field;
      case ParameterType.CENTRO:
        field.props.label = translate.instant('user.fields.centre');
        return field;
      case ParameterType.DEPARTAMENTO:
        field.props.label = translate.instant('user.fields.department');
        field.props.maxLength = MAXLENGTH_DEPARTAMENTO;
        return field;
      case ParameterType.CICLO_FORMATIVO:
        field.props.label = translate.instant('user.fields.trainingCycle');
        return field;
      case ParameterType.CURSO:
        field.props.label = this.configService.isGarriguesCenter()
          ? translate.instant('user.fields.degree.interest')
          : translate.instant('user.fields.course');
        return field;
      case ParameterType.GRUPO_DEL_CURSO:
        field.props.label = translate.instant('user.fields.courseGroup');
        return field;
      case ParameterType.EMPRESA:
        field.props.label = translate.instant('user.fields.company');
        return field;
      case ParameterType.PUESTO:
        field.props.label = translate.instant('user.fields.position');
        return field;
      case ParameterType.BIBLIOMETRO:
        const _values: object[] = [
          { value: USER_OF.BIBLIOMETRO, label: translate.instant('user.fields.userOf.bibliometro') },
          { value: USER_OF.AIRPORT, label: translate.instant('user.fields.userOf.airport') },
          { value: USER_OF.SANTIAGO_LIBRARY, label: translate.instant('user.fields.userOf.santiagoLibrary') },
          { value: USER_OF.MALL_BARRIO, label: translate.instant('user.fields.userOf.mallBarrioIndependencia') },
          { value: USER_OF.NONE, label: translate.instant('user.fields.userOf.none') },
        ];

        field.type = 'select';
        field.props.label = translate.instant('user.fields.userOf.title');
        field.props.options = _values;
        return field;
      case ParameterType.VALUE_ALIAS:
      case ParameterType.VALID_DOMAINS:
      case ParameterType.VALIDATE_NEW_USER:
      case ParameterType.INVALID_DOMAINS:
        field.hide = true;
        return field;
      case ParameterType.PROVINCIA_MINISTERIO_COLOMBIA:
        field.props.attributes = { autocomplete: 'address-level2' };
        if (this.configService.isClientDibam()) {
          field.props.label = translate.instant('user.fields.state');
          field.hideExpression = (f) => f[ParameterType.PAIS] !== COUNTRY_CODE_CHILE;
        } else {
          field.props.label = translate.instant('user.fields.stateMC');
        }
        return field;
      case ParameterType.POBLACION_MINISTERIO_COLOMBIA:
        field.props.attributes = { autocomplete: 'home' };
        if (this.configService.isClientDibam()) {
          field.props.label = translate.instant('user.fields.commune');
          field.hideExpression = (f) => f[ParameterType.PAIS] !== COUNTRY_CODE_CHILE;
        } else {
          field.props.label = translate.instant('user.fields.cityMC');
        }
        return field;
      case ParameterType.CENTRO_MINISTERIO_COLOMBIA:
        field.props.attributes = { autocomplete: 'home' };
        field.props.label = translate.instant('user.fields.centreMC');
        return field;
      case ParameterType.CURSO_MINISTERIO_COLOMBIA:
        field.props.label = translate.instant('user.fields.courseMC');
        return field;
      case ParameterType.ROL:
        field.props.label = translate.instant('user.fields.rol');
        return field;
      case ParameterType.REGION:
        field.props.label = translate.instant('user.fields.region');
        if (this.configService.isClientDibam()) {
          field.hideExpression = (f) => f[ParameterType.PAIS] !== COUNTRY_CODE_CHILE;
        }
        return field;
      case ParameterType.NACIONALIDAD:
        field.props.label = translate.instant('user.fields.nationality');
        return field;
      case ParameterType.TIPO_INSTITUCION:
        field.props.label = translate.instant('user.fields.institutionType');
        return field;
      case ParameterType.ESCOLARIDAD:
        field.props.label = translate.instant('user.fields.scholarship');
        return field;
      case ParameterType.SITUACION_LABORAL:
        field.props.label = translate.instant('user.fields.employmentSituation');
        return field;
      case ParameterType.AFILIADO:
        field.props.label = translate.instant('user.fields.affiliate');
        return field;
      case ParameterType.CATEGORIA:
        field.props.label = translate.instant('user.fields.category');
        return field;
      default:
        this.logger.warn('[ACCOUNT] Unknown parameter', param);
    }
  }

  private getCountryLabel(param: UserParameter) {
    if (param.tipoParametro === ParameterType.PAIS) {
      if (this.configService.isClientDibam()) {
        return 'user.fields.countryOfResidence';
      }
      return 'user.fields.country';
    }
    return 'user.fields.countryOrigin';
  }

  private sortAvailableIdentifierOptionsDibam(options: any[]) {
    options.sort((a, _) => {
      if (Number(a.value) === IdentifierType.OTRO.valueOf()) {
        return -1;
      }
      return 0;
    });
  }

  private getGenderValues(parameterType: ParameterType.SEXO | ParameterType.SEXO_MINISTERIO_COLOMBIA) {
    const values = [
      { value: '0', label: this.localeService.instant('user.fields.gender.other') },
      { value: '1', label: this.localeService.instant('user.fields.gender.male') },
      { value: '2', label: this.localeService.instant('user.fields.gender.female') },
    ];

    if (parameterType === ParameterType.SEXO_MINISTERIO_COLOMBIA) {
      values.push(values.shift());
    }

    return values;
  }

  private getSaoPauloCepFieldConfig(): FormlyFieldConfig {
    return {
      key: ParameterType.CODIGO_POSTAL.toString(),
      type: 'input',
      props: {
        label: 'CEP (XXXXX-XXX)',
        attributes: {
          autocomplete: 'postal-code',
        },
      },
      validators: {
        cepValidator: cepValidator,
      },
      validation: {
        messages: {
          cepValidator: this.localeService.instant('login.invalidCpOrCep'),
        },
      },
      asyncValidators: {
        cep: {
          expression: (control: UntypedFormControl) =>
            new Promise((resolve) => {
              resolve(getCep(control, this.apiService, this.eventGetCep$, this.URL_API_GATEWAY_QUERY_CEP));
            }),
          message: this.localeService.instant('login.zipCodeNotExist'),
        },
      },
    };
  }

  private buildLoginUserTypes(): FormlyFieldConfig {
    return {
      key: 'userType',
      type: 'select',
      className: 'login-user-type-selector',
      props: {
        label: this.localeService.instant('user.fields.user.type'),
        required: true,
        options: [
          { value: 1, label: this.localeService.instant('user.fields.user.type.option1') },
          { value: 4, label: this.localeService.instant('user.fields.user.type.option2') },
          { value: 16, label: this.localeService.instant('user.fields.user.type.option3') },
          { value: 15, label: this.localeService.instant('user.fields.user.type.option4') },
          { value: 9, label: this.localeService.instant('user.fields.user.type.option5') },
          { value: 8, label: this.localeService.instant('user.fields.user.type.option6') },
          { value: 3, label: this.localeService.instant('user.fields.user.type.option7') },
          { value: 11, label: this.localeService.instant('user.fields.user.type.option8') },
          { value: 12, label: this.localeService.instant('user.fields.user.type.option9') },
          { value: 2, label: this.localeService.instant('user.fields.user.type.option10') },
          { value: 0, label: this.localeService.instant('user.fields.user.type.option11') },
          { value: 10, label: this.localeService.instant('user.fields.user.type.option12') },
          { value: 5, label: this.localeService.instant('user.fields.user.type.option13') },
        ],
        change: (_field, _) => {
          const formControl = _field.parent.fieldGroup[0].formControl as UntypedFormControl;
          formControl.updateValueAndValidity();
        },
      },
    };
  }
}
